<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useSessionStore } from '../stores/session'
import WikiHeaderActions from './WikiHeaderActions.vue'

// The header itself is just layout: the always-visible logo/go-home
// button plus the signed-in action bar, which lives in
// WikiHeaderActions since it owns nearly all of the header's state
// (session, source activity, lint/synthesis badges, agent sessions,
// the admin/mobile menus). Drawer triggers are the only state this
// view can't own — those flip refs in RouteShell — so the only emits
// left are `add-to-wiki` and `threads`, forwarded up unchanged from
// WikiHeaderActions.

const emit = defineEmits<{
  (e: 'add-to-wiki'): void
  (e: 'threads'): void
}>()

const router = useRouter()
const session = useSessionStore()
const { currentUser } = storeToRefs(session)

// Logo-click home. Skip the push if we're already there so the
// router doesn't fire a redundant navigation event. `fullPath` over
// `path` so a stray query string or hash on `/` doesn't trip the
// guard into pushing again.
async function goHome() {
  if (router.currentRoute.value.fullPath !== '/') await router.push('/')
}
</script>

<template>
  <header class="flex items-center justify-between px-5 py-2 border-b border-white/10 gap-3">
    <!-- The title doubles as a "go to catalog" control. Plain router-link
         to "/" isn't enough on mobile: the root URL auto-selects
         index.md so the detail pane hides the sidebar, and navigating
         to a URL we're already on wouldn't reflow the view. The local
         `goHome` handler also short-circuits when we're already on `/`. -->
    <button
      type="button"
      data-testid="go-home"
      class="no-underline shrink-0 bg-transparent border-0 p-0 cursor-pointer"
      @click="goHome"
    >
      <h1 class="text-xl font-bold text-white m-0">superbrain</h1>
    </button>

    <WikiHeaderActions v-if="currentUser" @add-to-wiki="emit('add-to-wiki')" @threads="emit('threads')" />
  </header>
</template>
